import React, {useCallback, useEffect, useRef, useState} from 'react'
import {Form, useFormikContext} from 'formik'
import {useTranslation} from 'react-i18next'
import styles from './OrderForm.module.css'
import {MailSettingsForm} from './MailSettingsForm'
import {CarrierForm} from './CarrierForm'
import {ClientDataForm} from './ClientDataForm'
import {ResponsiblePersonsForm} from './ResponsiblePersonsForm'
import {OrderInfoForm} from './OrderInfoForm'
import {ContainedButton} from 'components/Shared/ContainedButton'
import {OrderSchema} from './formSchema'
import UserRole from 'constants/UserRole'
import {useAuth} from 'context/auth'
import {FormikForm} from 'components/Shared/FormikForm'
import {fetchCarLocationServicesConnectionStatus} from 'services/CarService'
import {ScheduledEmailsFrequencyOptions} from 'components/Order/constants'
import Module from 'constants/customFieldModules'
import {FormRow} from 'components/Shared/CrudComponents'
import {AttachmentForm} from 'components/Order/OrderForms/OrderForm/AttachmentForm/AttachmentForm'
import {
  OrderPointsGroupForm,
  OrderPointType
} from 'components/Order/OrderForms/OrderForm/OrderPointsForm/OrderPointsGroupForm'
import {OrderTransactionsForm} from 'components/Order/OrderForms/OrderForm/OrderTransactionsForm/OrderTransactionsForm'
import {addContractorEmail} from 'services/ContractorService'
import {useErrorHandling} from 'context/errorHandling'
import {TransactionType} from './OrderTransactionsForm/OrderTransactionsGroupForm/OrderTransactionForm/TransactionType'
import {isDeepEqual} from 'react-use/lib/util'
import {FERRY_PASSAGE_TRANSACTION_SERVICE_TYPE_ID} from 'constants/serviceTypes'
import {orderVerificationWarnings, orderVerificationWarningsInit} from "../../utils";

const ENTER_KEY_CODE = 13

function onKeyDown(keyEvent) {
  if ((keyEvent.charCode || keyEvent.keyCode) === ENTER_KEY_CODE) {
    keyEvent.preventDefault()
  }
}

const SelectedCarLocationServicesConnectionStatusChecker = () => {
  const {values, setFieldValue} = useFormikContext()
  const carId = values.carrier.internalCar?.carId
  useEffect(() => {
    let subscribed = true
    setFieldValue('emailNotificationSettings.scheduledEmailsDisabled', false)
    setFieldValue('emailNotificationSettings.scheduledEmailsFrequency', ScheduledEmailsFrequencyOptions.MorningAndEvening)
    if (carId) {
      fetchCarLocationServicesConnectionStatus(carId)
        .then(response => {
          if (subscribed && response.status?.connected === false) {
            setFieldValue('emailNotificationSettings.scheduledEmailsDisabled', true)
            setFieldValue('emailNotificationSettings.scheduledEmailsFrequency', ScheduledEmailsFrequencyOptions.Never)
          }
        })
    }
    return () => subscribed = false
  }, [carId, setFieldValue])

  return null
}

export function OrderForm({initialValues, entityId, initialAttachments, onSubmit, title, lockId}) {
  const [isSubmitting, setSubmitting] = useState(false)
  const [costFerryPassageRelation, setCostFerryPassageRelation] = useState({})
  const {handleError} = useErrorHandling()
  const {t} = useTranslation()
  const {user, hasRole} = useAuth()
  const formRef = useRef()
  const [client, setClient] = useState(null)
  const [carrier, setCarrier] = useState(null)
  const [formChanges, setFormChanges] = useState(0)
  const [carrierVerificationWarnings, setCarrierVerificationWarnings] = useState({
    ...orderVerificationWarningsInit
  })
  const {globalConfig} = useAuth()

  function costMatchesFerryPassage(cost, ferryPassageDetails) {
    return cost.serviceTypeId === FERRY_PASSAGE_TRANSACTION_SERVICE_TYPE_ID &&
      cost.contractorId === ferryPassageDetails.contractorId &&
      cost.paymentDate === ferryPassageDetails.paymentDate &&
      isDeepEqual(cost.price, ferryPassageDetails.price)
  }

  function ferryPassageOrCostAlreadyInRelation(newRelation, ferryPassage, index) {
    return newRelation.hasOwnProperty(ferryPassage.id)
      || Object.values(newRelation).some(i => i === index)
  }

  const getInitialCostFerryPassageRelations = useCallback(() => {
    const initialCosts = initialValues.transactions.costs
    const initialFerryPassages = initialValues.points.filter(p => p.type === OrderPointType.FerryPassage)
    let newRelation = {}
    initialCosts.forEach((cost, index) => {
      initialFerryPassages.forEach(ferryPassage => {
        const ferryPassageDetails = ferryPassage.ferryPassageDetails
        if (costMatchesFerryPassage(cost, ferryPassageDetails)) {
          if (ferryPassageOrCostAlreadyInRelation(newRelation, ferryPassage, index)) {
            return
          }
          newRelation[ferryPassage.id] = index
        }
      })
    })
    setCostFerryPassageRelation(newRelation)
  }, [initialValues.transactions.costs, initialValues.points])

  useEffect(() => {
    getInitialCostFerryPassageRelations()
  }, [getInitialCostFerryPassageRelations])

  const setBccEmail = (values, setFieldValue) => (previousPerson, newPerson) => {
    if (newPerson) {
      const rest = previousPerson ?
        values.contractorInfo.bccEmails.filter(email => email !== previousPerson.email) : values.contractorInfo.bccEmails
      setFieldValue('contractorInfo.bccEmails', rest.includes(newPerson.email) ? rest : [...rest, newPerson.email])
    } else if (previousPerson && !newPerson) {
      setFieldValue('contractorInfo.bccEmails', values.contractorInfo.bccEmails.filter(email => email !== previousPerson.email))
    }
  }

  const handlePayerSelect = (values, setFieldValue) => (payerId, payer) => {
    const previousPayer = values.contractorInfo.payerId

    const incomes = values.transactions.incomes
    incomes.forEach(income => {
      if (!income.contractorId || income.contractorId === previousPayer) {
        income.contractorId = payerId
        income.paymentDays = payer?.incomePaymentDueDays || 30
      }
    })
    setFieldValue('transactions.incomes', incomes)
  }

  function setCostContractor(values, setFieldValue, carrierId, carrier) {
    const previousCarrier = values.carrier.id

    const costs = values.transactions.costs
    costs.forEach(cost => {
      if (!cost.contractorId || cost.contractorId === previousCarrier) {
        cost.contractorId = carrierId
        if (carrier != null) {
          cost.paymentDays = carrier?.costPaymentDueDays || 30;
        }
      }
    })
    setFieldValue('transactions.costs', costs)
  }

  const setDispatcherId = (values, setFieldValue, carrierId) => {
    const previousCarrierId = values.carrier.id
    if (!previousCarrierId && !!carrierId) {
      setFieldValue('dispatcherId', hasRole(UserRole.Dispatcher) ? user.id : null)
    }
  }

  const handleCarrierSelect = (values, setFieldValue) => (carrierId) => {
    setDispatcherId(values, setFieldValue, carrierId)
    setCostContractor(values, setFieldValue, carrierId, null)
  }

  const handleSubmit = (values, onSuccess) => {
    setSubmitting(true)
    values.contractorInfo.emails.forEach(email =>
      addContractorEmail(values.contractorInfo.clientId, email)
        .catch(handleError)
    )

    onSubmit(values, onSuccess)
      .finally(() => setSubmitting(false))
  }

  const mapFerryPassageToNewTransaction = ferryPassage => {
    const {contractorId, price, paymentDays} = ferryPassage

    return {
      transactionType: TransactionType.Cost,
      contractorId,
      serviceTypeId: FERRY_PASSAGE_TRANSACTION_SERVICE_TYPE_ID,
      price: {
        amount: price?.amount,
        currencyCode: price?.currencyCode
      },
      paymentDays: paymentDays,
      comment: ''
    }
  }

  const costRemovedHandler = (removedCostIndex) => {
    setCostFerryPassageRelation(
      Object.keys(costFerryPassageRelation)
        .map(key => {
          const value = costFerryPassageRelation[key]
          return value > removedCostIndex
            ? {key, value: (value - 1)}
            : {key, value}
        })
        .reduce((acc, obj) => ({
          ...acc, [obj.key]: obj.value
        }), {})
    )
  }

  const setCostRelationToFerryPassage = (ferryPassageId, values) => {
    const newCostIndex = values.transactions.costs.length
    setCostFerryPassageRelation(oldRelation => {
      const newRelation = oldRelation
      newRelation[ferryPassageId] = newCostIndex
      return newRelation
    })
  }

  const removeCostRelatedToFerryPassage = (ferryPassageId, setFieldValue, values) => {
    const costs = values.transactions.costs
    const costToRemoveIndex = costFerryPassageRelation[ferryPassageId]
    const costToRemove = costs[costToRemoveIndex]
    setFieldValue('transactions.costs', costs.filter(cost => cost !== costToRemove))
    delete costFerryPassageRelation[ferryPassageId]
    costRemovedHandler(costToRemoveIndex)
  }

  const isTransactionDisabled = index => Object.values(costFerryPassageRelation).some(i => i === index)

  const setCostValueBasedOnFerryPassage = useCallback((updatedFerryPassageId, setFieldValue, values) => (updatedFerryPassage) => {
    const updatedCostIndex = costFerryPassageRelation[updatedFerryPassageId]
    if (updatedCostIndex === null || updatedCostIndex === undefined) return

    const previousCost = values.transactions.costs[updatedCostIndex]
    const newCost = mapFerryPassageToNewTransaction(updatedFerryPassage)
    if (!isDeepEqual(previousCost, newCost)) {
      setFieldValue(`transactions.costs.${updatedCostIndex}`, newCost)
    }
  }, [costFerryPassageRelation])

  const onFullClientData = () => (client) => {
    setClient(client)
  }

  const onFullCarrierData = (values, setFieldValue) => (carrier) => {
    setCostContractor(values, setFieldValue, carrier.id, carrier)
    setCarrier(carrier)
  }

  useEffect(() => {
    const orderHv = formRef.current?.values?.highValue
    const loadingPoints = formRef.current?.values.points.filter(point => point.type === OrderPointType.Loading)
    const deliveryPoints = formRef.current?.values.points.filter(point => point.type === OrderPointType.Unloading)
    setCarrierVerificationWarnings(orderVerificationWarnings(client, carrier, orderHv, loadingPoints, deliveryPoints, globalConfig?.carrierVerificationValidityAfterDischarge))
  }, [client, carrier, formChanges, globalConfig])

  const handleFormChange = (values) => {
    if (values && formRef?.current?.values) {
      if (values.highValue !== formRef?.current?.values?.highValue ||
        pointsDiffString(values.points) !== pointsDiffString(formRef?.current?.values?.points)) {
        setFormChanges(formChanges + 1)

      }
    }
  }

  function pointsDiffString(points) {
    return points.filter(p => !!p.deliveryPointDetails).map(p => [p.deliveryPointDetails?.fromDate, p.deliveryPointDetails?.toDate]).toString()
  }


  return <div className={styles.formWrapper}>
    <FormikForm
      innerRef={formRef}
      initialValues={initialValues}
      validateOnChange={false}
      validateOnBlur={true}
      enableReinitialize={true}
      lockId={lockId}
      validationSchema={OrderSchema}
      onSubmit={handleSubmit}
      customFields={{
        module: Module.Orders,
        entityId: entityId
      }}>
      {({setFieldValue, values}) =>
        (
          <Form onKeyDown={onKeyDown} onChange={handleFormChange(values)}>
            <div className={styles.title}>
              <h1>{t(title)}</h1>
              <ResponsiblePersonsForm onPersonChanged={setBccEmail(values, setFieldValue)}/>
            </div>
            <div>

            </div>
            <FormRow className={styles.formRowContainer}>
              <ClientDataForm onPayerSelect={handlePayerSelect(values, setFieldValue)}
                              onFullClientData={onFullClientData()}/>
              <CarrierForm fieldName="carrier"
                           isEditing={!!entityId}
                           onCarrierSelect={handleCarrierSelect(values, setFieldValue)}
                           onFullCarrierData={onFullCarrierData(values, setFieldValue)}
                           carrierVerificationWarnings={carrierVerificationWarnings}
              />
              <OrderInfoForm/>
              <AttachmentForm initialAttachments={initialAttachments}/>
            </FormRow>
            <div className={styles.divider}/>
            <OrderTransactionsForm name="transactions"
                                   onContractorCreated={() => {
                                   }}
                                   onCostRemoved={(costId) => costRemovedHandler(costId)}
                                   isTransactionDisabled={isTransactionDisabled}
            />
            <div className={styles.divider}/>
            <OrderPointsGroupForm name="points"
                                  onFerryPassageChanged={(ferryPassageId) => setCostValueBasedOnFerryPassage(ferryPassageId, setFieldValue, values)}
                                  onFerryPassageCreated={(ferryPassageId) => setCostRelationToFerryPassage(ferryPassageId, values)}
                                  onFerryPassageDeleted={(ferryPassageId) => removeCostRelatedToFerryPassage(ferryPassageId, setFieldValue, values)}
            />
            <SelectedCarLocationServicesConnectionStatusChecker/>
            <MailSettingsForm/>
            <ContainedButton
              type="submit"
              isDisabled={isSubmitting}
              className={styles.submitButton}
              label={t('form.order.submit')}
            />
          </Form>
        )}
    </FormikForm>
  </div>
}
